if (!customElements.get('blog-posts-external')) {
  customElements.define(
    'blog-posts-external',
    class BlogPostsExternal extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.feedUrl = this.dataset.feedUrl;
        this.limit = parseInt(this.dataset.limit) || 4;
        this.cacheMinutes = parseInt(this.dataset.cacheMinutes) || 60;
        this.titleFontClass = this.dataset.titleFontClass || '';
        this.readMoreLabel = this.dataset.readMoreLabel || 'Read more';
        this.showExcerpt = this.dataset.showExcerpt === 'true';
        this.showReadMore = this.dataset.showReadMore === 'true';
        this.arrowSvg = this.dataset.arrowSvg || '';

        this.slider = this.querySelector('slider-component');
        this.container = this.querySelector('.slider-component__grid');
        this.countEl = this.querySelector('[data-count]');
        this.cacheKey = `theme:blog-posts-external:${this.feedUrl}:${this.limit}`;

        // Debug on when: data-debug="true" setting, ?blog-debug in the URL, or the theme editor.
        try {
          this.debug =
            this.dataset.debug === 'true' ||
            new URLSearchParams(location.search).has('blog-debug') ||
            (window.Shopify && Shopify.designMode === true);
        } catch (e) {
          this.debug = this.dataset.debug === 'true';
        }
        this.log('constructed', {
          sectionId: this.sectionId,
          feedUrl: this.feedUrl,
          limit: this.limit,
          cacheMinutes: this.cacheMinutes,
          cacheKey: this.cacheKey,
          hasSlider: !!this.slider,
          hasContainer: !!this.container,
          hasCountEl: !!this.countEl,
        });
      }

      log(...args) {
        if (this.debug) console.log('%c[blog-posts-external]', 'color:#7c3aed;font-weight:bold', ...args);
      }

      warn(...args) {
        console.warn('[blog-posts-external]', ...args);
      }

      connectedCallback() {
        this.log('connectedCallback');
        if (!this.feedUrl) {
          this.warn('No feed URL set on this section — nothing to fetch. Set the "Feed URL" setting in the theme editor.');
          return;
        }
        if (!this.container) {
          this.warn('Could not find .slider-component__grid inside the section — markup changed?');
          return;
        }
        this.render();
      }

      get isDesignMode() {
        return window.Shopify && Shopify.designMode === true;
      }

      readCache() {
        try {
          const raw = localStorage.getItem(this.cacheKey);
          if (!raw) {
            this.log('cache miss', this.cacheKey);
            return null;
          }
          const parsed = JSON.parse(raw);
          const ageMin = Math.round((Date.now() - parsed.ts) / 60000);
          this.log('cache hit', { ageMinutes: ageMin, posts: parsed.posts && parsed.posts.length });
          return parsed;
        } catch (e) {
          this.warn('cache read failed', e);
          return null;
        }
      }

      writeCache(posts) {
        try {
          localStorage.setItem(this.cacheKey, JSON.stringify({ ts: Date.now(), posts }));
          this.log('cache written', { posts: posts.length });
        } catch (e) {
          this.warn('cache write failed (storage full or unavailable)', e);
        }
      }

      async render() {
        const cached = this.readCache();
        const fresh = cached && Date.now() - cached.ts < this.cacheMinutes * 60 * 1000;
        this.log('render', { fresh: !!fresh, isDesignMode: this.isDesignMode });

        if (fresh && !this.isDesignMode) {
          this.log('serving from fresh cache, skipping fetch');
          this.paint(cached.posts);
          return;
        }

        try {
          const posts = await this.fetchPosts();
          this.writeCache(posts);
          this.paint(posts);
        } catch (e) {
          if (cached) {
            this.warn('fetch failed — falling back to stale cache', e);
            this.paint(cached.posts);
          } else {
            this.warn('fetch failed and no cache available — section stays hidden', e);
          }
        }
      }

      async fetchPosts() {
        const url = new URL(this.feedUrl);
        url.searchParams.set('per_page', this.limit);
        url.searchParams.set('_embed', '1');

        this.log('fetching', url.toString());
        const t0 = performance.now();
        const res = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
        this.log('response', {
          status: res.status,
          ok: res.ok,
          ms: Math.round(performance.now() - t0),
          contentType: res.headers.get('content-type'),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        this.log('parsed JSON', { isArray: Array.isArray(data), count: Array.isArray(data) ? data.length : null });

        if (!Array.isArray(data)) throw new Error('Feed did not return an array of posts');

        const posts = data.slice(0, this.limit).map((post) => {
          const media = post._embedded && post._embedded['wp:featuredmedia'] && post._embedded['wp:featuredmedia'][0];
          const sizes = (media && media.media_details && media.media_details.sizes) || {};
          const preferred = sizes.medium_large || sizes.large || sizes.full;
          const srcset = Object.values(sizes)
            .filter((s) => s && s.source_url && s.width)
            .map((s) => `${s.source_url} ${s.width}w`)
            .join(', ');

          if (media && media.code) {
            this.warn('featured media not embedded for post', post.id, '- image will fall back to placeholder');
          }

          return {
            title: this.decode(post.title && post.title.rendered),
            link: post.link,
            date: post.date,
            excerpt: this.stripHtml(post.excerpt && post.excerpt.rendered).slice(0, 220),
            image: preferred ? preferred.source_url : media && media.source_url,
            imageWidth: preferred ? preferred.width : null,
            imageHeight: preferred ? preferred.height : null,
            srcset,
            alt: (media && media.alt_text) || '',
          };
        });
        this.log('mapped posts', posts);
        return posts;
      }

      decode(html) {
        if (!html) return '';
        const el = document.createElement('textarea');
        el.innerHTML = html;
        return el.value.trim();
      }

      stripHtml(html) {
        if (!html) return '';
        const el = document.createElement('div');
        el.innerHTML = html;
        return (el.textContent || '').replace(/\s+/g, ' ').replace(/\[…\]|Continue reading.*/i, '').trim();
      }

      formatDate(value) {
        const d = new Date(value);
        if (isNaN(d)) return '';
        return d.toLocaleDateString(document.documentElement.lang || undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      }

      cardMarkup(post) {
        const mediaBlock = post.image
          ? `<div class="card-article__media-wrapper card__media-adapt">
              <img
                class="card-border-radius"
                src="${post.image}"
                ${post.srcset ? `srcset="${post.srcset}"` : ''}
                sizes="(min-width: 64em) 25vw, 100vw"
                ${post.imageWidth ? `width="${post.imageWidth}"` : ''}
                ${post.imageHeight ? `height="${post.imageHeight}"` : ''}
                alt="${this.escapeAttr(post.alt)}"
                loading="lazy"
                style="aspect-ratio: 547 / 463; object-fit: cover;"
              >
            </div>`
          : `<div class="card-article__media-wrapper card__media-wrapper media-wrapper--placeholder" style="--image-ratio: 1;"></div>`;

        const excerptBlock =
          this.showExcerpt && post.excerpt
            ? `<div class="card-article__excerpt body-lg">${this.escapeHtml(post.excerpt)}</div>`
            : '';

        const readMoreBlock = this.showReadMore
          ? `<span aria-hidden="true" class="card-article__read-more body-lg border-link">
              <span class="button__label">${this.escapeHtml(this.readMoreLabel)}</span>
              ${this.arrowSvg}
            </span>`
          : '';

        return `<div class="card card-article expand-link">
          <h3>
            <a class="expand-link__link" href="${post.link}" target="_blank" rel="noopener">
              <span class="visually-hidden">${this.escapeHtml(post.title)}</span>
            </a>
          </h3>
          ${mediaBlock}
          <div class="card-article__main">
            <div class="card-article__date caption-xs">${this.formatDate(post.date)}</div>
            <p class="card-article__title ${this.titleFontClass}" aria-hidden="true">${this.escapeHtml(post.title)}</p>
            ${excerptBlock}
            ${readMoreBlock}
          </div>
        </div>`;
      }

      escapeHtml(str) {
        return String(str || '').replace(/[&<>"']/g, (c) => {
          return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
        });
      }

      escapeAttr(str) {
        return this.escapeHtml(str).replace(/\n/g, ' ');
      }

      paint(posts) {
        if (!posts || !posts.length) {
          this.warn('paint called with no posts — section stays hidden');
          return;
        }
        this.log('painting', posts.length, 'cards');
        this.container.innerHTML = '';

        posts.slice(0, this.limit).forEach((post, index) => {
          const li = document.createElement('li');
          li.className = 'slider-component__item';
          li.id = `Slide-${this.sectionId}-${index + 1}`;
          li.innerHTML = this.cardMarkup(post);
          this.container.appendChild(li);
        });

        if (this.countEl) this.countEl.style.setProperty('--count', this.container.children.length);
        this.removeAttribute('hidden');
        this.log('mounting slider', { hasSlider: !!this.slider });
        this.slider?.mount();
        this.log('done');
      }
    }
  );
}
